/**
 * AdminOperationalSummaryCard — COA-10 G5
 * =========================================
 * Compact 5-tile summary of the clinic's operational state, surfaced
 * on `/clinic/admin` Overview. Reads from GET /api/clinic/admin/operational-summary.
 * Strictly read-only; each tile deep-links into the existing Coordinator
 * Workspace tab that owns the underlying data.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, ShieldCheck, AlertOctagon, ClipboardList, AlertCircle, RefreshCw, ChevronRight } from 'lucide-react';
import { api } from '../../utils/api';

const TILE_DEFS = [
  { key: 'active_cases',      labelKey: 'provider.operationalSummary.activeCases',      tone: 'sky',     icon: ClipboardList, dest: '/clinic/operations', initialView: 'cases' },
  { key: 'pending_documents', labelKey: 'provider.operationalSummary.pendingDocuments', tone: 'amber',   icon: FileText,      dest: '/clinic/operations', initialView: 'missing-docs' },
  { key: 'pending_consents',  labelKey: 'provider.operationalSummary.pendingConsents',  tone: 'amber',   icon: ShieldCheck,   dest: '/clinic/operations', initialView: 'missing-consents' },
  { key: 'escalations',       labelKey: 'provider.operationalSummary.escalations',      tone: 'rose',    icon: AlertOctagon,  dest: '/clinic/operations', initialView: 'escalations' },
  { key: 'overdue',           labelKey: 'provider.operationalSummary.overdue',          tone: 'rose',    icon: AlertCircle,   dest: '/clinic/operations', initialView: 'cases' },
];

const TONE_CLASSES = {
  sky:    'bg-sky-50 border-sky-200 text-sky-700 hover:border-sky-400',
  amber:  'bg-amber-50 border-amber-200 text-amber-800 hover:border-amber-400',
  rose:   'bg-rose-50 border-rose-200 text-rose-700 hover:border-rose-400',
  slate:  'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-400',
};

export default function AdminOperationalSummaryCard({ hideHeader = false, refreshKey = 0 } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/clinic/admin/operational-summary');
      if (data?.success) setData(data);
      else setError(t('provider.operationalSummary.loadError'));
    } catch {
      setError(t('provider.operationalSummary.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchSummary(); }, [fetchSummary, refreshKey]);

  const totals = data?.totals || {};

  return (
    <section
      className={hideHeader ? '' : 'bg-white border border-slate-200 rounded-xl mb-3'}
      data-testid="admin-operational-summary"
    >
      {!hideHeader && (
        <header className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-[13px] font-semibold text-slate-900">{t('provider.operationalSummary.title')}</h2>
            <p className="text-[11px] text-slate-500">
              {t('provider.operationalSummary.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={fetchSummary}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-200 hover:bg-slate-50 text-slate-700"
            data-testid="admin-summary-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            {t('provider.common.refresh')}
          </button>
        </header>
      )}

      {error ? (
        <div className="px-4 py-4 text-[12px] text-rose-600" data-testid="admin-summary-error">{error}</div>
      ) : (
        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 ${hideHeader ? '' : 'p-3'}`}>
          {TILE_DEFS.map((tile) => {
            const Icon = tile.icon;
            const label = t(tile.labelKey);
            const value = totals[tile.key] ?? (loading ? '—' : 0);
            return (
              <button
                key={tile.key}
                type="button"
                onClick={() => navigate(tile.dest, { state: { initialView: tile.initialView } })}
                data-testid={`admin-summary-tile-${tile.key}`}
                aria-label={t('provider.operationalSummary.openInWorkspace', { label })}
                title={t('provider.operationalSummary.openTooltip', { label })}
                className={`group text-left rounded-lg border px-3 py-2.5 cursor-pointer transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-400 ${TONE_CLASSES[tile.tone]}`}
              >
                <div className="flex items-center justify-between">
                  <Icon className="h-3.5 w-3.5 opacity-70" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
                    {label}
                  </span>
                </div>
                <div className="mt-1 flex items-end justify-between">
                  <div className="text-2xl font-bold leading-tight">
                    {value}
                  </div>
                  {/* TREATX-COORDINATOR-IA-CONSOLIDATION-02:
                      Small unobtrusive navigation indicator — fades in
                      stronger on hover/focus. Same `ChevronRight` icon
                      already used elsewhere in the dashboard (e.g.
                      Provider Admin "More" toggle pre-cleanup, and
                      summary list-row links). No new icon set, no new
                      design language. */}
                  <ChevronRight
                    className="h-3.5 w-3.5 opacity-40 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity"
                    aria-hidden="true"
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
